import React, { useEffect, useRef, useState } from "react";
import { scenarioLabApi } from "../../../services/phase1Api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import {
  FlaskConical, Loader2, Plus, RotateCcw, Trash2, ShieldCheck, Save,
  Building2, School, Users, ClipboardCheck, BarChart3, CheckCircle2,
  Sparkles, PencilLine, Archive, PlayCircle, X, Share2, Copy, Eye,
} from "lucide-react";

const STAGE_META = {
  organizations: { label: "Organizations", icon: Building2, hint: "Authorizers, CMOs, districts", opt: "count", optLabel: "How many orgs?", def: 3 },
  schools:       { label: "Schools", icon: School, hint: "Schools under each org", opt: "per_org", optLabel: "Schools per org", def: 3 },
  users:         { label: "Users", icon: Users, hint: "School admin accounts", opt: "per_school", optLabel: "Users per school", def: 1 },
  diagnostics:   { label: "Diagnostics", icon: ClipboardCheck, hint: "One diagnostic per school", opt: null, def: 0 },
  responses:     { label: "Responses", icon: Sparkles, hint: "Realistic scored answers", opt: null, def: 0, heavy: true },
  reporting:     { label: "Reporting", icon: BarChart3, hint: "Dashboards read generated data", opt: null, def: 0 },
};

const ModeBadge = ({ mode }) => {
  if (mode === "generated") return <span className="text-[11.5px] font-semibold px-2 py-0.5 rounded-full bg-blue-100 text-blue-700">Generated</span>;
  if (mode === "manual") return <span className="text-[11.5px] font-semibold px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">Manual</span>;
  return <span className="text-[11.5px] font-semibold px-2 py-0.5 rounded-full bg-amber-50 text-amber-600">Pending</span>;
};

export default function ScenarioLabPage({ session }) {
  const [access, setAccess] = useState(null);
  const [scenarios, setScenarios] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [selId, setSelId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [newName, setNewName] = useState("");
  const [opts, setOpts] = useState({});
  const [busy, setBusy] = useState("");
  const [job, setJob] = useState(null);        // { stage, percent, status }
  const [validation, setValidation] = useState(null);
  const pollRef = useRef(null);

  const loadLists = () => {
    scenarioLabApi.listScenarios(session).then((d) => setScenarios(d.scenarios || [])).catch(() => {});
    scenarioLabApi.listTemplates(session).then((d) => setTemplates(d.templates || [])).catch(() => {});
  };
  const loadDetail = (id) => {
    if (!id) { setDetail(null); return; }
    scenarioLabApi.getScenario(id, session).then(setDetail).catch(() => toast.error("Failed to load scenario"));
  };

  useEffect(() => {
    scenarioLabApi.access(session)
      .then((a) => { setAccess(a); if (a.has_access) loadLists(); })
      .catch(() => setAccess({ has_access: false }));
    return () => pollRef.current && clearInterval(pollRef.current);
  }, [session]); // eslint-disable-line

  useEffect(() => { loadDetail(selId); setValidation(null); }, [selId]); // eslint-disable-line

  const createScenario = async () => {
    if (!newName.trim()) return toast.error("Name your scenario first");
    setBusy("create");
    try {
      const s = await scenarioLabApi.createScenario({ name: newName.trim() }, session);
      setNewName(""); loadLists(); setSelId(s.id);
      toast.success("Scenario created");
    } catch { toast.error("Could not create scenario"); }
    setBusy("");
  };

  const pollJob = (id, jobId, stage) => {
    pollRef.current && clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const j = await scenarioLabApi.jobStatus(id, jobId, session);
        setJob({ stage, percent: j.percent, status: j.status });
        if (j.status === "completed" || j.status === "failed") {
          clearInterval(pollRef.current);
          setJob(null);
          if (j.status === "completed") toast.success(`Generated ${j.result?.responses || 0} responses`);
          else toast.error("Response generation failed");
          loadDetail(id); loadLists();
        }
      } catch { clearInterval(pollRef.current); setJob(null); }
    }, 1200);
  };

  const generate = async (stage) => {
    const meta = STAGE_META[stage];
    const payload = {};
    if (meta.opt) payload[meta.opt] = Number(opts[stage] ?? meta.def) || meta.def;
    setBusy(stage);
    try {
      const r = await scenarioLabApi.generateStage(selId, stage, payload, session);
      if (r.async) { setJob({ stage, percent: 0, status: "running" }); pollJob(selId, r.job_id, stage); }
      else { toast.success(`${meta.label} generated`); loadDetail(selId); loadLists(); }
    } catch (e) { toast.error(e?.response?.data?.detail || "Generation failed"); }
    setBusy("");
  };

  const setManual = async (stage) => {
    setBusy(stage);
    try { await scenarioLabApi.manualStage(selId, stage, session); loadDetail(selId); }
    catch { toast.error("Failed"); }
    setBusy("");
  };

  const resetScenario = async () => {
    if (!window.confirm("Delete ALL generated data for this scenario? The scenario shell stays.")) return;
    setBusy("reset");
    try { const r = await scenarioLabApi.resetScenario(selId, session); toast.success("Scenario reset"); loadDetail(selId); loadLists(); setValidation(null);
      console.log("reset deleted", r.deleted); }
    catch { toast.error("Reset failed"); }
    setBusy("");
  };

  const removeScenario = async () => {
    if (!window.confirm("Permanently delete this scenario and its data?")) return;
    setBusy("delete");
    try { await scenarioLabApi.deleteScenario(selId, session); toast.success("Scenario deleted"); setSelId(null); loadLists(); }
    catch { toast.error("Delete failed"); }
    setBusy("");
  };

  const validate = async () => {
    setBusy("validate");
    try { const v = await scenarioLabApi.validateScenario(selId, session); setValidation(v);
      toast[v.isolation_ok ? "success" : "error"](v.isolation_ok ? "Isolation verified — no leak into production" : "Isolation check FAILED"); }
    catch { toast.error("Validation failed"); }
    setBusy("");
  };

  const saveTemplate = async () => {
    const name = window.prompt("Template name", detail?.name ? `${detail.name} template` : "Scenario template");
    if (!name) return;
    setBusy("tpl");
    try { await scenarioLabApi.saveTemplate({ name, scenario_id: selId }, session); toast.success("Template saved"); loadLists(); }
    catch { toast.error("Save failed"); }
    setBusy("");
  };

  const restoreTemplate = async (id) => {
    setBusy("restore");
    try { const s = await scenarioLabApi.restoreTemplate(id, session); toast.success("Scenario created from template"); loadLists(); setSelId(s.id); }
    catch { toast.error("Restore failed"); }
    setBusy("");
  };

  const toggleTour = async (enabled) => {
    setBusy("tour");
    try {
      await scenarioLabApi.setTour(selId, enabled, session);
      toast.success(enabled ? "Tour link is live" : "Tour sharing disabled");
      loadDetail(selId);
    } catch { toast.error("Could not update tour"); }
    setBusy("");
  };

  const tourUrl = detail?.tour_token ? `${window.location.origin}/scenario-tour/${detail.tour_token}` : "";
  const copyTour = () => { navigator.clipboard.writeText(tourUrl); toast.success("Tour link copied"); };

  if (access === null) return <div className="p-8 flex items-center gap-2 text-foreground/60"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>;
  if (!access.has_access) return (
    <div className="p-8 max-w-lg" data-testid="scenario-lab-no-access">
      <div className="flex items-center gap-2 text-foreground/70"><ShieldCheck className="h-5 w-5" /> Scenario Lab access is restricted to Super Admins.</div>
    </div>
  );

  const counts = detail?.counts || {};

  return (
    <div className="p-6 lg:p-8 max-w-6xl mx-auto" data-testid="scenario-lab-page">
      <div className="flex items-center gap-3 mb-1">
        <div className="h-10 w-10 rounded-xl bg-indigo-600 text-white flex items-center justify-center"><FlaskConical className="h-5 w-5" /></div>
        <div>
          <h1 className="text-2xl font-bold text-foreground">Scenario Lab</h1>
          <p className="text-sm text-foreground/50">Build isolated demo, pilot, QA & training data — never touches production.</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-6">
        {/* left: scenarios + templates */}
        <div className="space-y-4">
          <div className="rounded-xl border border-border bg-card p-4">
            <div className="text-xs font-semibold uppercase tracking-wider text-foreground/50 mb-2">New Scenario</div>
            <div className="flex gap-2">
              <Input data-testid="scenario-name-input" placeholder="e.g. West Coast Pilot" value={newName} onChange={(e) => setNewName(e.target.value)} onKeyDown={(e) => e.key === "Enter" && createScenario()} />
              <Button data-testid="scenario-create-btn" onClick={createScenario} disabled={busy === "create"}>
                {busy === "create" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
              </Button>
            </div>
          </div>

          <div className="rounded-xl border border-border bg-card p-4">
            <div className="text-xs font-semibold uppercase tracking-wider text-foreground/50 mb-2">Scenarios</div>
            <div className="space-y-1" data-testid="scenario-list">
              {scenarios.length === 0 && <div className="text-sm text-foreground/40 py-3">No scenarios yet.</div>}
              {scenarios.map((s) => (
                <button key={s.id} data-testid={`scenario-item-${s.id}`} onClick={() => setSelId(s.id)}
                  className={`w-full text-left px-3 py-2 rounded-lg transition-colors ${selId === s.id ? "bg-indigo-50 border border-indigo-200" : "hover:bg-muted"}`}>
                  <div className="text-sm font-medium text-foreground truncate">{s.name}</div>
                  <div className="text-[11.5px] text-foreground/45">{s.record_count} records · {s.status}</div>
                </button>
              ))}
            </div>
          </div>

          <div className="rounded-xl border border-border bg-card p-4">
            <div className="text-xs font-semibold uppercase tracking-wider text-foreground/50 mb-2 flex items-center gap-1"><Archive className="h-3.5 w-3.5" /> Saved Templates</div>
            <div className="space-y-1" data-testid="template-list">
              {templates.length === 0 && <div className="text-sm text-foreground/40 py-2">No templates saved.</div>}
              {templates.map((t) => (
                <div key={t.id} className="flex items-center justify-between px-2 py-1.5 rounded-lg hover:bg-muted">
                  <span className="text-sm text-foreground truncate">{t.name}</span>
                  <div className="flex gap-1">
                    <Button size="sm" variant="ghost" className="h-7 px-2" data-testid={`template-restore-${t.id}`} onClick={() => restoreTemplate(t.id)}><PlayCircle className="h-3.5 w-3.5" /></Button>
                    <Button size="sm" variant="ghost" className="h-7 px-2 text-red-500" onClick={async () => { await scenarioLabApi.deleteTemplate(t.id, session); loadLists(); }}><X className="h-3.5 w-3.5" /></Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* right: guided stages */}
        <div className="lg:col-span-2">
          {!detail ? (
            <div className="rounded-xl border border-dashed border-border bg-card/50 p-12 text-center text-foreground/40" data-testid="scenario-empty">
              Select or create a scenario to begin the guided setup.
            </div>
          ) : (
            <div className="rounded-xl border border-border bg-card p-5" data-testid="scenario-detail">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h2 className="text-lg font-bold text-foreground">{detail.name}</h2>
                  <div className="text-[11.5px] text-foreground/45">{counts._total || 0} records generated</div>
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button size="sm" variant="outline" data-testid="scenario-validate-btn" onClick={validate} disabled={busy === "validate"}><ShieldCheck className="h-3.5 w-3.5 mr-1" /> Validate</Button>
                  <Button size="sm" variant="outline" data-testid="scenario-save-template-btn" onClick={saveTemplate} disabled={busy === "tpl"}><Save className="h-3.5 w-3.5 mr-1" /> Save Template</Button>
                  <Button size="sm" variant="outline" className="text-amber-600" data-testid="scenario-reset-btn" onClick={resetScenario} disabled={busy === "reset"}><RotateCcw className="h-3.5 w-3.5 mr-1" /> Reset</Button>
                  <Button size="sm" variant="outline" className="text-red-500" data-testid="scenario-delete-btn" onClick={removeScenario} disabled={busy === "delete"}><Trash2 className="h-3.5 w-3.5 mr-1" /> Delete</Button>
                </div>
              </div>

              {validation && (
                <div className={`mb-4 rounded-lg p-3 text-sm ${validation.isolation_ok ? "bg-emerald-50 text-emerald-800" : "bg-red-50 text-red-700"}`} data-testid="scenario-validation">
                  <div className="font-semibold flex items-center gap-1">{validation.isolation_ok ? <CheckCircle2 className="h-4 w-4" /> : <X className="h-4 w-4" />} {validation.isolation_ok ? "Isolation verified — zero scenario records leak into production reports." : "Isolation check failed."}</div>
                  <div className="text-xs mt-1 opacity-80">Scenario records remaining after this check: {validation.remaining?._total || 0}</div>
                </div>
              )}

              {/* Guided Tour sharing */}
              <div className="mb-4 rounded-lg border border-indigo-100 bg-indigo-50/50 p-3" data-testid="scenario-tour-panel">
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <div className="flex items-center gap-2 min-w-0">
                    <Share2 className="h-4 w-4 text-indigo-600 shrink-0" />
                    <div className="min-w-0">
                      <div className="text-sm font-semibold text-foreground">Guided Tour Link</div>
                      <div className="text-[11.5px] text-foreground/50">Public, read-only walkthrough for pilots &amp; trainees — no login needed.</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {detail.tour_enabled && (
                      <span className="text-[11.5px] text-foreground/50 flex items-center gap-1" data-testid="tour-view-count">
                        <Eye className="h-3.5 w-3.5" /> {detail.tour_views || 0} views
                      </span>
                    )}
                    <Button size="sm" variant={detail.tour_enabled ? "outline" : "default"}
                      className={detail.tour_enabled ? "text-red-500" : ""}
                      data-testid="scenario-tour-toggle-btn" onClick={() => toggleTour(!detail.tour_enabled)} disabled={busy === "tour"}>
                      {busy === "tour" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : (detail.tour_enabled ? "Disable" : "Enable Sharing")}
                    </Button>
                  </div>
                </div>
                {detail.tour_enabled && tourUrl && (
                  <div className="flex items-center gap-2 mt-2">
                    <Input readOnly value={tourUrl} className="h-8 text-xs bg-white" data-testid="tour-link-input" onFocus={(e) => e.target.select()} />
                    <Button size="sm" variant="outline" className="h-8" onClick={copyTour} data-testid="tour-copy-btn"><Copy className="h-3.5 w-3.5" /></Button>
                    <a href={tourUrl} target="_blank" rel="noreferrer"><Button size="sm" variant="outline" className="h-8" data-testid="tour-open-btn"><PlayCircle className="h-3.5 w-3.5" /></Button></a>
                  </div>
                )}
              </div>

              <div className="space-y-2">
                {Object.keys(STAGE_META).map((stage, i) => {
                  const meta = STAGE_META[stage];
                  const st = detail.stages?.[stage] || {};
                  const Icon = meta.icon;
                  const running = job?.stage === stage;
                  return (
                    <div key={stage} className="rounded-lg border border-border p-3" data-testid={`stage-${stage}`}>
                      <div className="flex items-center justify-between gap-3 flex-wrap">
                        <div className="flex items-center gap-3 min-w-0">
                          <div className="h-8 w-8 rounded-lg bg-muted flex items-center justify-center text-foreground/60 shrink-0"><Icon className="h-4 w-4" /></div>
                          <div className="min-w-0">
                            <div className="text-sm font-semibold text-foreground flex items-center gap-2">
                              <span className="text-foreground/40 text-xs">{i + 1}.</span> {meta.label} <ModeBadge mode={st.mode} />
                            </div>
                            <div className="text-[11.5px] text-foreground/45">{meta.hint}{st.mode === "generated" ? ` · ${st.count} created` : ""}</div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {meta.opt && st.mode !== "generated" && (
                            <Input type="number" min={1} className="w-16 h-8" data-testid={`stage-${stage}-count`}
                              placeholder={String(meta.def)} value={opts[stage] ?? ""} onChange={(e) => setOpts({ ...opts, [stage]: e.target.value })} />
                          )}
                          {stage !== "reporting" && (
                            <Button size="sm" variant="ghost" className="h-8 text-foreground/60" data-testid={`stage-${stage}-manual-btn`} onClick={() => setManual(stage)} disabled={busy === stage}>
                              <PencilLine className="h-3.5 w-3.5 mr-1" /> Manual
                            </Button>
                          )}
                          <Button size="sm" className="h-8" data-testid={`stage-${stage}-generate-btn`} onClick={() => generate(stage)} disabled={busy === stage || running}>
                            {(busy === stage || running) ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5 mr-1" />}
                            {stage === "reporting" ? "Mark Ready" : "Generate"}
                          </Button>
                        </div>
                      </div>
                      {running && (
                        <div className="mt-2" data-testid={`stage-${stage}-progress`}>
                          <div className="h-1.5 rounded-full bg-muted overflow-hidden">
                            <div className="h-full bg-indigo-500 transition-all" style={{ width: `${job.percent || 0}%` }} />
                          </div>
                          <div className="text-[11.5px] text-foreground/45 mt-1">Generating scored responses… {job.percent || 0}%</div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
